import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { getCapacityDay, getCapacityDaySummary, setCapacityDay, setCapacityDayDelta } from './apis'
import {
  GetCapacityDayReq,
  GetCapacityDayRes,
  GetCapacityDaySummaryReq,
  GetCapacityDaySummaryRes,
  SetCapacityDayDeltaReq,
  SetCapacityDayReq,
} from './types'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { ScheduleMenuCode } from '@/utils/menuCodeHeaders'

const PATH = ['capacity']
export const GET_CAPACITY_DAY_SUMMARY = [...PATH, 'GET_CAPACITY_DAY_SUMMARY']
export const GET_CAPACITY_DAY = [...PATH, 'GET_CAPACITY_DAY']

// 조회 키 끝에 화면코드를 둔다 — gi27·gi28 이 같은 날짜로 캐시를 같이 쓰면 헤더 없이 캐시로 끝나거나 다른 화면 코드로 재조회된다.
//  무효화는 앞부분([...KEY, { date }]) 일치라 그대로 걸린다.

/** 날짜별 전체 센터 케파 summary 조회 */
export const useGetCapacityDaySummary = (
  params: PartialBy<GetCapacityDaySummaryReq, 'date'>,
  menuCode: ScheduleMenuCode,
): UseQueryResult<GetCapacityDaySummaryRes, Error> => {
  return useQuery({
    queryKey: [...GET_CAPACITY_DAY_SUMMARY, params, menuCode],
    queryFn: () => getCapacityDaySummary(params as GetCapacityDaySummaryReq, menuCode),
    select: ({ data }) => data,
    enabled: !!params.date,
  })
}

/** 날짜별 센터 케파 조회 (Suspense) */
export const useSuspenseGetCapacityDay = (
  params: GetCapacityDayReq,
  menuCode: ScheduleMenuCode,
): UseSuspenseQueryResult<GetCapacityDayRes, Error> => {
  return useSuspenseQuery({
    queryKey: [...GET_CAPACITY_DAY, params, menuCode],
    queryFn: () => getCapacityDay(params, menuCode),
    select: ({ data }) => data,
    staleTime: API_COMMON_CACHE_TIME,
    gcTime: API_COMMON_CACHE_TIME,
  })
}

/** 날짜별 기준케파, 제한여부 수정 */
export const useSetCapacityDay = (menuCode: ScheduleMenuCode): UseMutationResult<unknown, Error, SetCapacityDayReq> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: SetCapacityDayReq) => setCapacityDay(req, menuCode),
    onSuccess: (_, req) => {
      const { date, warehouseCode, capacityType } = req

      queryClient.invalidateQueries({ queryKey: [...GET_CAPACITY_DAY, { date, warehouseCode, capacityType }] })
      queryClient.invalidateQueries({ queryKey: [...GET_CAPACITY_DAY_SUMMARY, { date }] })
    },
  })
}

/** 날짜별 케파 증차감 수정 */
export const useSetCapacityDayDelta = (menuCode: ScheduleMenuCode): UseMutationResult<unknown, Error, SetCapacityDayDeltaReq> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: SetCapacityDayDeltaReq) => setCapacityDayDelta(req, menuCode),
    onSuccess: (_, req) => {
      const { date, warehouseCode, capacityType } = req

      queryClient.invalidateQueries({ queryKey: [...GET_CAPACITY_DAY, { date, warehouseCode, capacityType }] })
      queryClient.invalidateQueries({ queryKey: [...GET_CAPACITY_DAY_SUMMARY, { date }] })
    },
  })
}
